import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  Dumbbell,
  Search,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  X,
  Clock,
  Layers,
  Flame,
} from "lucide-react";
import WorkoutTemplateForm from "@/components/trainer/WorkoutTemplateForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DIFFICULTY_COLORS = {
  beginner: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  intermediate: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  advanced: "bg-purple-500/10 text-purple-400 border-purple-500/20",
};

const GOAL_LABELS = {
  strength: "Strength",
  hypertrophy: "Hypertrophy",
  endurance: "Endurance",
  fat_loss: "Fat Loss",
  weight_loss: "Weight Loss",
  general_fitness: "General Fitness",
};

export default function AdminProgramsSection({
  onBack,
  initialFilter = "all",
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["admin_workout_templates"],
    queryFn: () => base44.entities.WorkoutTemplate.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.WorkoutTemplate.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_workout_templates"] });
      setShowForm(false);
      setEditingTemplate(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.WorkoutTemplate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_workout_templates"] });
      setShowForm(false);
      setEditingTemplate(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.WorkoutTemplate.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_workout_templates"] });
      setDeletingTemplate(null);
    },
  });

  const handleFormSubmit = (data) => {
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (t.name && t.name.toLowerCase().includes(q)) ||
        (t.description && t.description.toLowerCase().includes(q));

      if (!matchSearch) return false;

      const diff = (t.difficulty || "").toLowerCase();
      if (filter === "all") return true;
      return diff === filter;
    });
  }, [templates, search, filter]);

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
            aria-label="Back to Command Center"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Workout Plans</h1>
            <p className="text-[11px] text-slate-400">
              {filteredTemplates.length} of {templates.length} programs
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingTemplate(null);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition-all cursor-pointer"
          data-testid="create-program-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Create Program</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search programs by name or description…"
          className="w-full pl-9 pr-8 py-2 rounded-xl bg-[#071326] border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-400"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Difficulty Filter Horizontal Pills */}
      <div className="flex items-center gap-1.5">
        {[
          { id: "all", label: "All Levels" },
          { id: "beginner", label: "Beginner" },
          { id: "intermediate", label: "Intermediate" },
          { id: "advanced", label: "Advanced" },
        ].map((pill) => {
          const isActive = filter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setFilter(pill.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? "bg-sky-500 text-slate-950 shadow-sm"
                  : "bg-white/5 text-slate-400 hover:text-white border border-white/5"
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {/* Program Cards List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="rounded-2xl p-8 bg-[#071326] border border-white/10 text-center space-y-2">
          <Dumbbell className="w-8 h-8 text-slate-500 mx-auto" />
          <p className="text-xs font-bold text-white">No workout programs found</p>
          <p className="text-[11px] text-slate-400">
            {search ? "No programs match your search criteria." : "Create your first workout program."}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredTemplates.map((template) => {
            const exCount = Array.isArray(template.exercises) ? template.exercises.length : 0;
            const diff = (template.difficulty || "intermediate").toLowerCase();
            const diffColor = DIFFICULTY_COLORS[diff] || DIFFICULTY_COLORS.intermediate;
            const goalName = GOAL_LABELS[template.goal] || template.goal || "Fitness";

            return (
              <div
                key={template.id}
                className="p-3.5 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/40 transition-all shadow-sm"
                data-testid={`program-card-${template.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-bold text-white truncate">
                        {template.name}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${diffColor}`}>
                        {diff}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 text-[9px] font-bold">
                        {goalName}
                      </span>
                    </div>

                    {template.description && (
                      <p className="text-[11px] text-slate-400 line-clamp-1 mb-2">
                        {template.description}
                      </p>
                    )}

                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                      <span className="flex items-center gap-1">
                        <Layers className="w-3 h-3 text-sky-400" />
                        {exCount} exercises
                      </span>
                      {template.days_per_week && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-emerald-400" />
                          {template.days_per_week} days / week
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowForm(true);
                      }}
                      className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition-all cursor-pointer"
                      aria-label="Edit Program"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeletingTemplate(template)}
                      className="p-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-white/10 active:scale-95 transition-all cursor-pointer"
                      aria-label="Delete Program"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Workout Template Form */}
      {showForm && (
        <WorkoutTemplateForm
          open={showForm}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setShowForm(false);
              setEditingTemplate(null);
            }
          }}
          initialData={editingTemplate}
          onSubmit={handleFormSubmit}
        />
      )}

      {/* Delete Confirmation */}
      {deletingTemplate && (
        <AlertDialog open onOpenChange={() => setDeletingTemplate(null)}>
          <AlertDialogContent className="bg-[#030A17] border border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Program</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-300 text-xs">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-white">{deletingTemplate.name}</span>? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate(deletingTemplate.id)}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
